import { useMemo, useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs.jsx";
import { Input } from "../ui/input.jsx";
import { Sparkles, ClipboardCopy } from "lucide-react";
import "../../global.css"

export default function AgentPanel({ context, messages, onInsertSuggestion }) {
  const [loading, setLoading] = useState(null);
  const [result, setResult] = useState({});
  const [prompt, setPrompt] = useState("");

  const transcript = useMemo(() => messages.map(m => `- ${m.author}: ${m.text}`).join("\n"), [messages]);

  async function gen(kind) {
    setLoading(kind);
    try {
      let p = "";
      if (kind === "summary") p = `Summarize in 5 bullets the ${context} discussion so far.\n\n${transcript}`;
      else if (kind === "tasks") p = `Extract action items with owners if present. Return 5 concise bullet points.\n\n${transcript}`;
      else if (kind === "suggest") p = `Suggest 3 short, helpful replies for this ${context}:\n\n${transcript}`;
      else if (kind === "email") p = `Draft a crisp follow-up email (subject + body) summarizing decisions and next steps based on: \n\n${transcript}`;
      else p = prompt;
      const res = await fetch("/api/v1/ai/generate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prompt: p, n: kind === "suggest" ? 3 : 1, system: "Be concise and useful." }) });
      const data = await res.json();
      const text = (data.suggestions?.[0] || "").trim();
      if (kind === "summary") setResult(r => ({ ...r, summary: text }));
      if (kind === "tasks") setResult(r => ({ ...r, tasks: text.split(/\n|\r/).filter(Boolean).map(s => s.replace(/^[-*]\s*/, "")) }));
      if (kind === "suggest") setResult(r => ({ ...r, suggestions: data.suggestions || [] }));
      if (kind === "email") setResult(r => ({ ...r, email: text }));
      if (kind === "custom") setResult(r => ({ ...r, summary: text }));
    } finally { setLoading(null); }
  }

  return (
    <Card className="p-3">
      <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4"/><b>AI Assistant</b></div>
      <Tabs defaultValue="insights">
        <TabsList className="grid grid-cols-4">
          <TabsTrigger value="insights">Insights</TabsTrigger>
          <TabsTrigger value="suggestions">Replies</TabsTrigger>
          <TabsTrigger value="email">Email</TabsTrigger>
          <TabsTrigger value="custom">Custom</TabsTrigger>
        </TabsList>
        <TabsContent value="insights" className="space-y-2">
          <div className="flex gap-2"><Button size="sm" onClick={()=>gen("summary")} disabled={loading!==null}>{loading==="summary"?"Generating…":"Summarize"}</Button><Button size="sm" variant="secondary" onClick={()=>gen("tasks")} disabled={loading!==null}>{loading==="tasks"?"Generating…":"Tasks"}</Button></div>
          {result.summary && <CopyBlock label="Summary" text={result.summary} />}
          {result.tasks && result.tasks.length>0 && (
            <div>
              <div className="text-xs font-medium mb-1">Action items</div>
              <ul className="list-disc pl-5 text-sm space-y-1">
                {result.tasks.map((t,i)=> <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}
        </TabsContent>
        <TabsContent value="suggestions" className="space-y-2">
          <Button size="sm" onClick={()=>gen("suggest")} disabled={loading!==null}>{loading==="suggest"?"Generating…":"Suggest replies"}</Button>
          <div className="flex flex-wrap gap-2">
            {(result.suggestions || []).map((s,i)=> (
              <button key={i} className="rounded-full border px-3 py-1 text-xs hover:bg-secondary" onClick={()=> onInsertSuggestion?.(s)}>{s}</button>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="email" className="space-y-2">
          <Button size="sm" onClick={()=>gen("email")} disabled={loading!==null}>{loading==="email"?"Generating…":"Draft follow-up"}</Button>
          {result.email && <CopyBlock label="Email" text={result.email} />}
        </TabsContent>
        <TabsContent value="custom" className="space-y-2">
          <div className="flex gap-2"><Input placeholder="Ask anything about this discussion" value={prompt} onChange={(e)=>setPrompt(e.target.value)}/><Button onClick={()=>gen("custom")} disabled={!prompt.trim() || loading!==null}>Ask</Button></div>
          {result.summary && <CopyBlock label="Answer" text={result.summary} />}
        </TabsContent>
      </Tabs>
    </Card>
  );
}

function CopyBlock({ label, text }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-md border p-2">
      <div className="text-xs font-medium mb-1 flex items-center justify-between"><span>{label}</span><button onClick={()=>{navigator.clipboard.writeText(text).then(()=>{setCopied(true); setTimeout(()=>setCopied(false), 1200);});}} className="inline-flex items-center text-xs text-blue-600"><ClipboardCopy className="h-3.5 w-3.5 mr-1"/>{copied?"Copied":"Copy"}</button></div>
      <pre className="whitespace-pre-wrap text-sm">{text}</pre>
    </div>
  );
}
